import React, { useContext, useEffect, useState } from 'react';
import { FieldHookConfig, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import { DesktopDatePicker, LocalizationProvider, MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { SxProps } from '@mui/system';
// eslint-disable-next-line import/no-duplicates
import { parseISO } from 'date-fns';
// eslint-disable-next-line import/no-duplicates
import 'date-fns/locale';
import { UIContext } from '../../context/UIContext';
import { UserContext } from '../../context/UserContext';
import { formatDate, setFieldOnSessionStorage } from '../../helpers/helpers';

type DatePickerProps = FieldHookConfig<string> & {
  label: string;
  id: string;
  formName?: string;
  objectId?: string;
  required?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  helperText?: string;
  error?: string;
  clearError?: (errorKey: string) => void;
  fieldSx?: SxProps;
};

const FormikDatePicker = (props: DatePickerProps) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(props);
  const {
    label,
    id,
    formName,
    objectId = '',
    required = false,
    disablePast = false,
    disableFuture = false,
    helperText = '',
    error = '',
    clearError,
    fieldSx,
  } = props;
  const { isMobile } = useContext(UIContext);
  const { localeFns } = useContext(UserContext);
  const { setValue, setTouched } = helpers;

  const dateValue = field.value ? new Date(field.value) : null;
  const [fieldDateContent, setFieldDateContent] = useState(dateValue);

  useEffect(() => {
    setFieldDateContent(field.value ? new Date(field.value) : null);
  }, [field.value]);

  const changeField = (value: Date | null) => {
    clearError?.(id);

    if (!value || Number.isNaN(value.getTime())) {
      setFieldDateContent(null);
      setValue('');
      return;
    }

    const year = value.getFullYear();
    const isYearComplete = year >= 1900 && year <= 9999;

    if (!isYearComplete) {
      setFieldDateContent(value);
      return;
    }

    const newValue = `${formatDate(value)}`;

    if (formName) {
      setFieldOnSessionStorage(formName, objectId, field.name, newValue);
    }
    setFieldDateContent(value);
    setValue(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeFns}>
      {isMobile && (
        <MobileDatePicker
          label={label}
          value={fieldDateContent}
          onChange={changeField}
          disablePast={disablePast}
          disableFuture={disableFuture}
          slotProps={{
            textField: {
              required,
              error: Boolean(error) || (meta.touched && Boolean(meta.error)),
              helperText:
                (Boolean(error) && error) ||
                (meta.touched && meta.error && t(meta.error, { field: label })) ||
                helperText,
              sx: {
                marginBottom: { xs: '16px', sm: '24px' },
                ...fieldSx,
              },
            },
          }}
        />
      )}
      {!isMobile && (
        <DesktopDatePicker
          label={label}
          value={fieldDateContent}
          onChange={changeField}
          disablePast={disablePast}
          disableFuture={disableFuture}
          slotProps={{
            textField: {
              required,
              error: Boolean(error) || (meta.touched && Boolean(meta.error)),
              helperText:
                (Boolean(error) && error) ||
                (meta.touched && meta.error && t(meta.error, { field: label })) ||
                helperText,
              sx: {
                marginBottom: { xs: '16px', sm: '24px' },
                ...fieldSx,
              },
            },
          }}
        />
      )}
    </LocalizationProvider>
  );
};
export default FormikDatePicker;
